import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../lib/api";

/** Core KB 條目（v53） */
interface CoreKbEntry {
  id: number;
  lang: string;
  intent: string;
  answer_strategy: string;
  sample_questions: string | null;
  tenant_count: number;
  status: string;
  hit_count: number;
  wrong_count: number;
  created_at: number;
  approved_at: number | null;
}
interface CoreKbStats {
  active_count: number;
  pending_count: number;
  retired_count: number;
  hits_30d: number;
  user_msgs_30d: number;
}

function fmtDate(ms: number | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("en-GB", { timeZone: "Asia/Hong_Kong" });
}

/**
 * 🌀 Core KB 蒸餾飛輪（/system 新 TAB，2026-10-10 Captain 三拍板）：
 * 飛輪說明（管線六步＋檢索梯＋反饋環）＋審批（pending 批/退）＋條目表＋命中率＋手動蒸餾。
 * 租戶零感知——全部平台層動作，審批只有 Captain 一個環節。
 */
export default function CoreKbPanel() {
  const [status, setStatus] = useState("pending");
  const [entries, setEntries] = useState<CoreKbEntry[]>([]);
  const [stats, setStats] = useState<CoreKbStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [distilling, setDistilling] = useState(false);
  // 批量審批（Captain 2026-10-10「有冇全選」）：pending 視圖 checkbox＋全選＋批量批准
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);

  const load = useCallback(async (st: string) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await apiFetch(`/admin/core-kb?status=${st}`);
      const data = await res.json();
      setEntries(data.entries || []);
      if (data.stats) setStats(data.stats);
    } catch {
      setMsg({ ok: false, text: "❌ 載入失敗" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(status);
  }, [status, load]);

  const act = async (id: number, action: "approve" | "reject") => {
    const verb = action === "approve" ? "批准上架" : "退回";
    if (!confirm(`確定${verb}條目 #${id}？${action === "approve" ? "上架後即刻進入全平台檢索梯。" : ""}`)) return;
    setBusyId(id);
    setMsg(null);
    try {
      const res = await apiFetch(`/admin/core-kb/${id}/${action}`, { method: "POST" });
      const data = await res.json();
      if (data.ok) {
        setMsg({ ok: true, text: action === "approve" ? `✅ 條目 #${id} 已上架（向量索引已寫入）` : `✅ 條目 #${id} 已退回` });
        load(status);
      } else {
        setMsg({ ok: false, text: `❌ ${data.error || "操作失敗"}` });
      }
    } catch {
      setMsg({ ok: false, text: "❌ 操作請求失敗" });
    } finally {
      setBusyId(null);
    }
  };

  const runDistill = async () => {
    if (!confirm("手動跑一輪蒸餾？（30 日窗全平台對話→聚類→提煉；成本約 DeepSeek 幾次調用）")) return;
    setDistilling(true);
    setMsg(null);
    try {
      const res = await apiFetch("/admin/core-kb/distill", { method: "POST" });
      const data = await res.json();
      if (data.ok) {
        setMsg({ ok: true, text: `✅ 蒸餾完成：樣本 ${data.samples}｜意圖群 ${data.clusters}｜合格 ${data.eligible}｜新條目 ${data.entries}${data.skipped ? `（${data.skipped}）` : ""}` });
        load(status);
      } else {
        setMsg({ ok: false, text: "❌ 蒸餾失敗（詳情見系統事件日誌）" });
      }
    } catch {
      setMsg({ ok: false, text: "❌ 蒸餾請求失敗" });
    } finally {
      setDistilling(false);
    }
  };

  const approveBatch = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!confirm(`確定批量批准上架 ${ids.length} 條？（每條會做向量嵌入並寫入 Core KB 索引）`)) return;
    setBatchBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch("/admin/core-kb/approve-batch", { method: "POST", body: JSON.stringify({ ids }) });
      const data = await res.json();
      if (data.ok) {
        const failed = (data.results || []).filter((r: { ok: boolean }) => !r.ok);
        setMsg({ ok: failed.length === 0, text: failed.length === 0 ? `✅ 已批量上架 ${data.approved} 條` : `⚠️ 成功 ${data.approved}/${data.total}——失敗：${failed.map((f: { id: number; error?: string }) => `#${f.id}${f.error ? `（${f.error}）` : ""}`).join("、")}` });
        setSelected(new Set());
        load(status);
      } else {
        setMsg({ ok: false, text: `❌ ${data.error || "批量審批失敗"}` });
      }
    } catch {
      setMsg({ ok: false, text: "❌ 批量審批請求失敗" });
    } finally {
      setBatchBusy(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === entries.length ? new Set() : new Set(entries.map((e) => e.id))));
  };

  const hitRate = stats && stats.user_msgs_30d > 0 ? ((stats.hits_30d / stats.user_msgs_30d) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-4">
      {/* 飛輪說明卡 */}
      <div className="p-4 bg-violet-900/20 border border-violet-800/30 rounded-xl">
        <h3 className="text-sm font-semibold text-violet-300 mb-2">🌀 Core KB 蒸餾飛輪——平台級「答法層」知識（越用越聰明＋越用越平）</h3>
        <div className="text-xs text-slate-300 leading-relaxed space-y-1.5">
          <p><b className="text-slate-100">管線（每週三 HKT 18:00 off-peak，全自動）</b>：全平台對話 30 日窗 → 匿名化閘（剝電話/電郵/編號）→ LLM 聚類意圖 → <b className="text-slate-100">≥3 租戶共現</b>先算通用 → LLM 提煉「答法結構」→ 待審批 → 上架入向量索引。</p>
          <p><b className="text-slate-100">檢索梯</b>：客人問題先查<b>租戶 KB</b>（事實層，優先）；租戶 KB 低信心先查<b>Core KB</b>（答法層——套入該租戶語氣由 LLM 組句）；都冇→LLM 自由生成（落入下輪蒸餾候選，飛輪閉口）。</p>
          <p><b className="text-slate-100">反饋環（零人工）</b>：條目命中記 hit；❌AI 答錯回溯記 wrong；wrong 率 ≥30%（≥10 hits）自動退役。</p>
          <p className="text-slate-400">🔒 鐵律：Core KB 只儲「點樣答」嘅模式，永不儲「答乜」嘅租戶事實；租戶零感知零參與。</p>
        </div>
      </div>

      {/* 指標 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-xl">
          <div className="text-[11px] text-slate-500">現役條目</div>
          <div className="text-lg font-bold text-emerald-400 tabular-nums">{stats?.active_count ?? "—"}</div>
        </div>
        <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-xl">
          <div className="text-[11px] text-slate-500">待審批</div>
          <div className="text-lg font-bold text-amber-400 tabular-nums">{stats?.pending_count ?? "—"}</div>
        </div>
        <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-xl">
          <div className="text-[11px] text-slate-500">已退役</div>
          <div className="text-lg font-bold text-slate-400 tabular-nums">{stats?.retired_count ?? "—"}</div>
        </div>
        <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-xl">
          <div className="text-[11px] text-slate-500">Core KB 命中（30 日）</div>
          <div className="text-lg font-bold text-violet-300 tabular-nums">{stats?.hits_30d ?? "—"}</div>
        </div>
        <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-xl">
          <div className="text-[11px] text-slate-500">命中率（hit/客人訊息）</div>
          <div className="text-lg font-bold text-violet-300 tabular-nums">{hitRate}%</div>
        </div>
      </div>

      {/* 控制列 */}
      <div className="flex flex-wrap items-center gap-2">
        {["pending", "active", "retired", "rejected"].map((st) => (
          <button
            key={st}
            onClick={() => setStatus(st)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              status === st
                ? "bg-emerald-600/20 border-emerald-500 text-emerald-300"
                : "bg-slate-950/40 border-slate-700/50 text-slate-400 hover:text-slate-200"
            }`}
          >
            {st === "pending" ? "⏳ 待審批" : st === "active" ? "✅ 現役" : st === "retired" ? "🗄️ 已退役" : "🚫 已退回"}
          </button>
        ))}
        <span className="flex-1" />
        {status === "pending" && entries.length > 0 && (
          <>
            <button
              onClick={toggleAll}
              className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
            >
              {selected.size === entries.length ? "☐ 取消全選" : "☑️ 全選"}
            </button>
            <button
              onClick={approveBatch}
              disabled={batchBusy || selected.size === 0}
              className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 text-white rounded-lg transition-colors"
            >
              {batchBusy ? "⏳ 上架中…" : `✅ 批准所選（${selected.size}）`}
            </button>
          </>
        )}
        <button
          onClick={runDistill}
          disabled={distilling}
          className="px-3 py-1.5 text-xs bg-violet-700 hover:bg-violet-600 disabled:bg-slate-700 text-white rounded-lg transition-colors"
        >
          {distilling ? "⏳ 蒸餾中（約 1-2 分鐘）…" : "🌀 手動蒸餾一輪"}
        </button>
      </div>

      {msg && <p className={`text-xs ${msg.ok ? "text-emerald-400" : "text-rose-400"}`}>{msg.text}</p>}

      {/* 條目表 */}
      {loading ? (
        <div className="text-sm text-slate-400">載入中…</div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-slate-500">
          {status === "pending" ? "未有待審批條目——每週三自動蒸餾，或撳「手動蒸餾一輪」。首輪需要全平台累積足夠對話（樣本 ≥10）。" : "此狀態未有條目。"}
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => {
            let samples: string[] = [];
            try { samples = JSON.parse(e.sample_questions || "[]"); } catch { /* empty */ }
            return (
              <div key={e.id} className="p-4 bg-slate-950/40 border border-slate-700/50 rounded-xl">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {e.status === "pending" && (
                    <input
                      type="checkbox"
                      checked={selected.has(e.id)}
                      onChange={() => toggleSelect(e.id)}
                      className="accent-emerald-500"
                      aria-label={`選擇條目 #${e.id}`}
                    />
                  )}
                  <span className="text-sm font-semibold text-slate-100">#{e.id} {e.intent}</span>
                  <span className="px-2 py-0.5 text-[11px] rounded-full bg-slate-800 text-slate-300">{e.lang}</span>
                  <span className="px-2 py-0.5 text-[11px] rounded-full bg-slate-800 text-slate-400">共現 {e.tenant_count} 租戶</span>
                  {e.status === "active" && (
                    <>
                      <span className="px-2 py-0.5 text-[11px] rounded-full bg-emerald-900/40 text-emerald-300">hit {e.hit_count}</span>
                      <span className={`px-2 py-0.5 text-[11px] rounded-full ${e.wrong_count > 0 ? "bg-rose-900/40 text-rose-300" : "bg-slate-800 text-slate-400"}`}>wrong {e.wrong_count}</span>
                    </>
                  )}
                  <span className="text-[11px] text-slate-500 ml-auto">{e.status === "pending" ? `建立 ${fmtDate(e.created_at)}` : `上架 ${fmtDate(e.approved_at)}`}</span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed mb-2">{e.answer_strategy}</p>
                {samples.length > 0 && (
                  <div className="text-[11px] text-slate-500 mb-2">
                    示例（已匿名化）：{samples.map((s, i) => <span key={i} className="inline-block bg-slate-900 rounded px-1.5 py-0.5 mr-1.5 mb-1">{s}</span>)}
                  </div>
                )}
                {e.status === "pending" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => act(e.id, "approve")}
                      disabled={busyId === e.id}
                      className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 text-white rounded-lg transition-colors"
                    >
                      ✅ 批准上架
                    </button>
                    <button
                      onClick={() => act(e.id, "reject")}
                      disabled={busyId === e.id}
                      className="px-3 py-1.5 text-xs bg-rose-900/50 hover:bg-rose-800/60 disabled:bg-slate-700 text-rose-200 rounded-lg transition-colors"
                    >
                      🚫 退回
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
